import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, LinearProgress } from '@mui/material';
import deepmerge from 'deepmerge';

import useNotification from '../../../../core/hooks/use_notification';
import { getAllWarehousesNotificationData, getWarehouseDocumentsNotificationData } from '../../../../core/utils/notification_utils';
import { Button } from '../../../common/button';
import { ROUTE_WAREHOUSE_DOCUMENT_FORM } from '../../../../core/constants';
import WarehouseDocumentFilters from './warehouse_documents_filters';
import { getWarehouseDocumentsAction, getWarehouseDocumentsState } from '../../../../features/inventory/get_warehouse_documents';
import PaginationControls from '../../../common/pagination_controls';
import { getDefaultPaginationQueryValues } from '../../../../core/utils/api_utils';
import WarehouseDocumentsList from './warehouse_documents_list';
import { getAllWarehousesAction, getAllWarehousesState } from '../../../../features/inventory/get_all_warehouses';
import PageHeader from '../../../common/page_header';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    query: '',
    type: [],
    warehouseId: []
};

const WarehouseDocumentsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showErrorMessage } = useNotification();
    const location = useLocation();

    const getWarehouseDocuments = useSelector(getWarehouseDocumentsState);
    const getAllWarehouses = useSelector(getAllWarehousesState);

    const [queryParams, setQueryParams] = useState(location?.state?.queryParams || initialQueryParams);

    const refreshList = useCallback(() => {
        dispatch(getWarehouseDocumentsAction(queryParams))
            .then(response => showErrorMessage(getWarehouseDocumentsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        dispatch(getAllWarehousesAction())
            .then(response => showErrorMessage(getAllWarehousesNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>

            <PageHeader
                title="Dokumenty magazynowe"
                actions={
                    <Button onClick={ () => navigate(ROUTE_WAREHOUSE_DOCUMENT_FORM) }>
                        Dodaj dokument magazynowy
                    </Button>
                }
            />

            {
                !getAllWarehouses?.loading && getAllWarehouses?.data && (
                    <WarehouseDocumentFilters
                        queryParams={ queryParams }
                        onSearchInputChange={ query => setQueryParams(prevState => ({ ...prevState, query })) }
                        onSelectWarehousesChange={ warehouseId => setQueryParams(prevState => ({
                            ...prevState,
                            warehouseId
                        })) }
                        onSelectTypesChange={ type => setQueryParams(prevState => ({ ...prevState, type })) }
                    />
                )
            }

            {
                (getWarehouseDocuments?.loading || getAllWarehouses?.loading) &&
                <Box sx={ { width: '100%' } }>
                    <LinearProgress/>
                </Box>
            }

            {
                (getWarehouseDocuments?.data?.content && getAllWarehouses?.data) &&
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <WarehouseDocumentsList queryParams={ queryParams }/>

                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getWarehouseDocuments?.data?.pageable?.pageNumber }
                            totalPages={ getWarehouseDocuments?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            }

        </Box>
    );
};

export default WarehouseDocumentsPage;